import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Hash, ArrowLeft, BookOpen } from 'lucide-react';
import { getAllPosts } from './blogPosts';
import { BlogPreviewCard } from './BlogPreviewCard';
import { SEOHead } from '../components/SEOHead';
import { useLocaleContext } from '../context/LocaleContext';
import { toTagSlug, toTagDisplay } from './categories';
import { TagIntro } from '../components/TagIntro';
import { TagRelatedContent } from '../components/TagRelatedContent';
import { getTagEnhancedDescription, generateTagSEOTitle } from '../data/tagDescriptions';

export const TagPage: React.FC = () => {
  const { tagSlug } = useParams<{ tagSlug: string }>();
  const { locale } = useLocaleContext();

  const allPosts = getAllPosts(locale);

  // Filter posts by tag
  const tagPosts = tagSlug
    ? allPosts.filter((post) =>
        post.tags.some((tag) => toTagSlug(tag) === tagSlug)
      )
    : [];

  // Get tag display name
  const tagDisplay = tagSlug ? toTagDisplay(tagSlug) : 'Tag';

  // Get all unique tags from all posts
  const allTags = Array.from(
    new Set(allPosts.flatMap((post) => post.tags))
  ).sort();

  // Get enhanced SEO data
  const localeKey = locale as 'en' | 'de' | 'pt-BR' | 'ms-MY';
  const enhanced = tagSlug ? getTagEnhancedDescription(tagDisplay, locale, tagPosts.length) : undefined;
  const seoTitle = enhanced?.seoTitle[localeKey] || enhanced?.seoTitle.en || generateTagSEOTitle(tagDisplay, locale);
  const seoDescription = enhanced?.seoDescription[localeKey] || enhanced?.seoDescription.en || `Articles tagged with "${tagDisplay}" on AI watermark removal, invisible text cleaning, and document privacy.`;

  useEffect(() => {
    // Guard against SSR
    if (typeof window === 'undefined' || !tagSlug) return;

    // Add JSON-LD structured data for CollectionPage
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      'name': `${tagDisplay} - AI Watermark Removal Blog`,
      'description': `Articles tagged with "${tagDisplay}" on AI watermark removal and text cleaning.`,
      'url': `https://gpt-watermark-remover.com/blog/tag/${tagSlug}`,
      'publisher': {
        '@type': 'Organization',
        'name': 'GROW FAST LTD.',
        'logo': {
          '@type': 'ImageObject',
          'url': 'https://gpt-watermark-remover.com/logo.png'
        }
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [tagSlug, tagDisplay]);

  if (!tagSlug) {
    return (
      <>
        <SEOHead
          title="Tag Not Found | GPT Watermark Remover"
          description="The requested tag was not found."
        />
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-charcoal-900 dark:text-white mb-4">
              Tag Not Found
            </h1>
            <p className="text-xl text-neutral-600 dark:text-neutral-300 mb-8">
              The tag you're looking for doesn't exist.
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 px-6 py-3 bg-accent-600 hover:bg-accent-700 text-white font-semibold rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Blog
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDescription}
      />

      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400 mb-8">
          <Link to="/blog" className="hover:text-accent-600 dark:hover:text-accent-400 transition-colors">
            Blog
          </Link>
          <span>/</span>
          <span className="text-charcoal-900 dark:text-white font-medium">
            #{tagDisplay}
          </span>
        </nav>

        {/* Enhanced Tag Introduction */}
        {tagSlug && (
          <TagIntro
            tag={tagDisplay}
            tagSlug={tagSlug}
            locale={locale}
            postCount={tagPosts.length}
          />
        )}

        {/* Related Content - Shown Before Posts for SEO */}
        {tagSlug && (
          <TagRelatedContent tag={tagDisplay} tagSlug={tagSlug} locale={locale} />
        )}

        {/* Blog Posts Grid */}
        {tagPosts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {tagPosts.map((post) => (
              <BlogPreviewCard key={post.slug} post={post} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 mb-16">
            <BookOpen className="w-16 h-16 mx-auto text-neutral-400 dark:text-neutral-600 mb-4" />
            <h2 className="text-2xl font-bold text-neutral-600 dark:text-neutral-400 mb-2">
              No articles with this tag yet
            </h2>
            <p className="text-neutral-500 dark:text-neutral-500 mb-8">
              Check back soon for new content.
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 px-6 py-3 bg-accent-600 hover:bg-accent-700 text-white font-semibold rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Browse All Articles
            </Link>
          </div>
        )}

        {/* Popular Tags Section */}
        <div className="bg-neutral-100 dark:bg-charcoal-800/50 rounded-xl p-8 border border-neutral-200 dark:border-charcoal-700">
          <h2 className="text-2xl font-bold text-charcoal-900 dark:text-white mb-6">
            Browse All Tags
          </h2>
          <div className="flex flex-wrap gap-3">
            {allTags.map((tag) => {
              const slug = toTagSlug(tag);
              const postCount = allPosts.filter((post) =>
                post.tags.some((t) => toTagSlug(t) === slug)
              ).length;
              const isCurrentTag = slug === tagSlug;

              return (
                <Link
                  key={slug}
                  to={`/blog/tag/${slug}`}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                    isCurrentTag
                      ? 'bg-accent-600 text-white shadow-lg'
                      : 'bg-white dark:bg-charcoal-700 hover:bg-neutral-50 dark:hover:bg-charcoal-600 border border-neutral-200 dark:border-charcoal-600 text-charcoal-900 dark:text-white'
                  }`}
                >
                  <Hash className="w-4 h-4" />
                  <span className="font-medium">{tag}</span>
                  <span
                    className={`text-xs px-2 py-0.5 rounded ${
                      isCurrentTag
                        ? 'bg-white/20 text-white'
                        : 'bg-neutral-100 dark:bg-charcoal-800 text-neutral-600 dark:text-neutral-400'
                    }`}
                  >
                    {postCount}
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default TagPage;
